import { parseIsoTp, findResponse, flowControlCommands } from './isotp.ts';

// Mode 22 — ReadDataByIdentifier.
//
// Standardised OBD-II carries no odometer. Manufacturers keep distance in their
// own modules under their own data identifiers, reachable by addressing a
// module directly and asking for a DID. The protocol is open; the mapping is
// not published.
//
// NOTHING HERE ASSERTS A READING. It sends candidate identifiers and returns
// what came back, scaled every plausible way, so a value can be checked against
// the dashboard before anything is trusted. A wrong odometer is worse than no
// odometer: it would accuse a clean vehicle of a rolled-back one.

export interface Mode22Probe {
  /** The reply stopped before the length it declared, so the value is unusable. */
  truncated: boolean;
  /** The vehicle refused the service outright: 7F 22 11. */
  refused?: boolean;
  /** The module addressed, as an 11-bit CAN header. */
  header: string;
  moduleName: string;
  did: string;
  /** Raw hex payload after the 62 + DID echo, or null if nothing answered. */
  raw: string | null;
  /** The same bytes read as an unsigned integer, most significant byte first. */
  value: number | null;
  /** Candidate interpretations. Which is right has to be confirmed by eye. */
  asKm: number | null;
  asMiles: number | null;
  /** Some modules count in units of 0.1 or 0.125 km. */
  scaled: { factor: number; km: number }[];
}

/**
 * Ford modules and their request headers.
 *
 * 7E0 is the powertrain module every OBD-II vehicle answers on. The others are
 * the standard diagnostic addresses for their systems; whether a given vehicle
 * populates them is exactly what this probe is for.
 */
/**
 * BMW, for the probe only.
 *
 * 7E0 and 7E1 are the standard OBD powertrain and transmission addresses that
 * every manufacturer answers on, so they are worth asking first. The rest are
 * BMW's own: the instrument cluster is where mileage actually lives on these
 * cars, and 6F1 is the tester address BMW's own tools use, with the target
 * module in the first data byte rather than in the header.
 *
 * That last part is why this is a probe and not a reader. Proper 6F1 addressing
 * needs the request framed differently, and until one of these answers there is
 * nothing to build on.
 */
export const BMW_MODULES: { header: string; name: string }[] = [
  { header: '7E0', name: 'DME — engine' },
  { header: '7E1', name: 'EGS — transmission' },
  { header: '660', name: 'KOMBI — instrument cluster' },
  { header: '640', name: 'BCM / FEM — body' },
  { header: '612', name: 'CAS — access system' },
  { header: '6F1', name: 'Tester address (BMW diagnostic)' },
];

/** Identifiers seen on BMW for mileage. Unverified, like Ford's once were. */
export const BMW_ODOMETER_CANDIDATES = ['DD01', '2000', '2001', 'F190', '1003'];

/**
 * A sweep of a 2023 F-250 found only 7E0 and 7E1 present. The three below it
 * answered nothing at all - they were addresses I guessed at when this was
 * written, and the probe has been asking them ever since for no reason.
 *
 * Left in place because another Ford may differ, and a wasted request costs a
 * second where a missing address costs a finding. But they are guesses, and
 * the two above them are not.
 */
export const FORD_MODULES: { header: string; name: string }[] = [
  { header: '7E0', name: 'PCM — powertrain' },
  { header: '7E1', name: 'TCM — transmission' },
  { header: '760', name: 'ABS' },
  { header: '720', name: 'IPC — instrument cluster' },
  { header: '726', name: 'BCM — body control' },
];

/**
 * Identifiers to try. Unverified.
 *
 * Each has been reported in enthusiast sources as a Ford distance value, and
 * none is confirmed against a vehicle whose dashboard we have read. That is the
 * purpose of the probe: run it, compare against the dash, and only then does a
 * pair earn a place in the scanner.
 */
/**
 * Confirmed on a 2023 F-250 Super Duty, 2026-09-01.
 *
 * The PCM answers DD01 with the odometer as whole kilometres, unsigned, no
 * scaling. Raw 017C16 read back as 97,302 km against a dashboard showing
 * 60,460.8 miles - 97,302 x 0.621371 = 60,460.6, which is the rounding a
 * whole-kilometre store produces and not a coincidence three digits deep.
 *
 * The transmission module answers DD01 with the identical value, which is
 * better evidence than one module agreeing with a dashboard: two independent
 * stores holding 017C16 is not a decoding coincidence.
 *
 * It also gives the mileage-fraud check something to stand on. Rolling back a
 * cluster is the usual trick, and it leaves the PCM and TCM untouched - so
 * modules disagreeing is the signal, and this is the first evidence that they
 * normally agree.
 *
 * Worth recording that it is not in the instrument cluster. The cluster drives
 * the display, so 720 was the obvious guess and it was wrong; the powertrain
 * and transmission modules hold the value.
 *
 * One vehicle is not a fleet. Treat this as known-good for Super Duty and still
 * a candidate elsewhere until a second truck agrees.
 */
export const FORD_ODOMETER_VERIFIED = {
  header: '7E0',
  module: 'PCM — powertrain',
  did: 'DD01',
  units: 'km' as const,
  factor: 1,
  evidence: '2023 F-250, raw 017C16 = 97,302 km = 60,461 mi, dashboard 60,460.8 mi',
};

/** Modules that answered DD01 with the same value. Agreement is the check. */
export const FORD_ODOMETER_CORROBORATING = ['7E0', '7E1'];

// DD01 first, because it is the one that has actually answered. The rest stay
// as candidates for models that do not use it.
export const FORD_ODOMETER_CANDIDATES = ['DD01', 'F124', '0404', '404C', '1E23'];

const UNSIGNED = (hex: string): number | null => {
  if (!hex || hex.length % 2 !== 0) return null;
  const n = parseInt(hex, 16);
  return Number.isFinite(n) ? n : null;
};

/**
 * Ask one module for one identifier.
 *
 * `command` is the raw ELM327 sender: setting a header changes where the next
 * request goes, so this is deliberately sequential rather than concurrent.
 */
export async function probeMode22(
  command: (cmd: string, timeoutMs?: number) => Promise<string>,
  header: string,
  moduleName: string,
  did: string,
): Promise<Mode22Probe> {
  const empty: Mode22Probe = {
    header, moduleName, did, raw: null, value: null, asKm: null, asMiles: null, scaled: [], truncated: false,
  };

  await command(`ATSH${header}`, 3_000).catch(() => '');
  // Flow control before the request, or the vehicle sends its first frame and
  // waits for a reply that never comes. Mode 22 answers routinely run past the
  // eight bytes a single frame holds, so this is not an edge case.
  for (const cmd of flowControlCommands(header)) await command(cmd, 3_000).catch(() => '');

  const reply = await command(`22${did}`, 5_000).catch(() => '');

  // Reassembled rather than string-matched. The old reading took whichever line
  // began with the right prefix and ignored the rest, so a long answer was
  // silently cut to its first frame - and a cut odometer is still a number
  // somebody would believe.
  // A negative response for service 22. 11 is "service not supported", 31 is
  // "request out of range", which is how a vehicle says it knows mode 22 but
  // not this identifier - a useful distinction when hunting for the right one.
  const flat = reply.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
  if (flat.includes('7F2211') || flat.includes('7F2212')) {
    return { ...empty, refused: true };
  }

  const messages = parseIsoTp(reply);
  const didValue = parseInt(did, 16);
  const found = findResponse(messages, 0x22, didValue);
  if (!found) return empty;

  const raw = found.data
    .slice(3)
    .map((b) => b.toString(16).toUpperCase().padStart(2, '0'))
    .join('');
  if (raw.length === 0) return { ...empty, truncated: !found.complete };
  if (!found.complete) {
    // Reported rather than used. An incomplete read is a fact about the
    // connection, not a measurement.
    return { ...empty, raw, truncated: true };
  }
  const value = UNSIGNED(raw);
  if (value === null) return { ...empty, raw };

  return {
    header,
    moduleName,
    did,
    raw,
    value,
    asKm: value,
    asMiles: Math.round(value * 0.621371),
    // The factors modules commonly use. Reading the right bytes with the wrong
    // factor is the likeliest way to get a plausible but wrong number.
    scaled: [0.1, 0.125, 1, 10].map((factor) => ({ factor, km: Math.round(value * factor) })),
    truncated: false,
  };
}

/**
 * Read the odometer, on any vehicle that answers.
 *
 * One route: PCM then TCM, DD01, whole kilometres. Verified on Ford, and tried
 * everywhere because asking costs nothing and a make that answers is a make
 * that works - refusing to ask only guarantees no mileage at all.
 *
 * What is guarded is believing the reply. A value has to be plausible as a road
 * distance, and where two modules answer they have to agree, before it reaches
 * the record. No candidate sweep and no scaling guesses.
 *
 * Both modules are read when both answer. They should agree; disagreement is
 * reported rather than resolved, since picking one would hide exactly the thing
 * worth noticing.
 */
export async function readOdometer(
  command: (cmd: string, timeoutMs?: number) => Promise<string>,
): Promise<{
  km: number | null;
  readings: { header: string; km: number }[];
  disagreement: boolean;
  /** The vehicle declined mode 22 outright, rather than staying silent. */
  refused?: boolean;
}> {
  const readings: { header: string; km: number }[] = [];
  let refused = false;

  for (const header of FORD_ODOMETER_CORROBORATING) {
    const probe = await probeMode22(command, header, header, FORD_ODOMETER_VERIFIED.did).catch(() => null);

    // 7F 22 11 is "service not supported": this vehicle does not do mode 22 at
    // all, so asking the next module is asking the same question twice. A
    // refusal is an answer, and reporting it as "no module answered" was the
    // same mistake made with mode 06 - a clear no, recorded as a silence.
    if (probe?.refused) {
      refused = true;
      break;
    }
    if (probe?.value != null && !probe.truncated) readings.push({ header, km: probe.value });
  }

  if (refused) return { km: null, readings, disagreement: false, refused: true };

  if (readings.length === 0) return { km: null, readings, disagreement: false };

  // Asking every make is free; storing a wrong answer is not. DD01 is verified
  // on Ford, and another manufacturer may use the same identifier for something
  // entirely different - a counter, a temperature, anything - which would land
  // in the record as mileage and quietly misjudge every service interval.
  //
  // So the value has to be plausible as a road distance before it is believed.
  // Under 100 km is a new car or a wrong field; over 1.6 million km is roughly
  // a million miles, which no vehicle in a members' garage has covered.
  const plausible = (km: number) => km >= 100 && km <= 1_600_000;
  const usable = readings.filter((r) => plausible(r.km));
  if (usable.length === 0) {
    return { km: null, readings, disagreement: false };
  }

  // A kilometre apart is rounding between modules; more is a real difference.
  const values = usable.map((r) => r.km);
  const disagreement = Math.max(...values) - Math.min(...values) > 1;

  // The lowest when they disagree. A rolled-back cluster reads low, so the
  // lower figure is the one less likely to have been inflated - and the flag
  // is what actually carries the warning.
  return { km: disagreement ? Math.min(...values) : values[0], readings, disagreement };
}

/** Every module and identifier, in order. Returns only the ones that answered. */
export async function findFordOdometer(
  command: (cmd: string, timeoutMs?: number) => Promise<string>,
  make: 'ford' | 'bmw' | 'both' = 'both',
): Promise<Mode22Probe[]> {
  const hits: Mode22Probe[] = [];
  const modules = make === 'bmw' ? BMW_MODULES : make === 'ford' ? FORD_MODULES : [...FORD_MODULES, ...BMW_MODULES];
  const dids = make === 'bmw' ? BMW_ODOMETER_CANDIDATES
    : make === 'ford' ? FORD_ODOMETER_CANDIDATES
    : [...new Set([...FORD_ODOMETER_CANDIDATES, ...BMW_ODOMETER_CANDIDATES])];

  // Deduplicated, since 7E0 and 7E1 are on both lists.
  const seen = new Set<string>();
  for (const mod of modules) {
    if (seen.has(mod.header)) continue;
    seen.add(mod.header);
    for (const did of dids) {
      const probe = await probeMode22(command, mod.header, mod.name, did);
      if (probe.raw !== null) hits.push(probe);
    }
  }
  // Leave the header where the rest of the scanner expects it.
  await command('ATSH7DF', 3_000).catch(() => '');
  return hits;
}

// ---- module presence ----

export interface ModulePresence {
  header: string;
  name: string;
  /** Whether anything is at this address, asked with mode 01. */
  state: 'answered' | 'silent';
  /**
   * Whether it speaks mode 22, asked separately.
   *
   * 'other-identifier' is the useful one: the module knows mode 22 and simply
   * does not hold F190, which means another identifier is worth trying. 'no'
   * means the service is unsupported and no identifier will ever work.
   */
  mode22: 'yes' | 'no' | 'other-identifier' | 'silent' | 'unknown';
  raw: string | null;
}

/**
 * Which modules are actually there.
 *
 * Separate from asking what they hold, and worth doing first. A refusal and a
 * silence look the same when you are hunting for an identifier - both give you
 * no data - but they mean opposite things. A module that refuses is present and
 * reachable and simply does not support what you asked; a module that is silent
 * is not on this bus at all, and no identifier will ever change that.
 *
 * Tonight's BMW made the point: 7E0 and 7E1 refused mode 22 while every other
 * address returned nothing, which is the difference between "wrong question"
 * and "nobody home". Twenty-five requests were spent discovering that, when one
 * per address would have said it.
 *
 * Knocks with 0100 - the mode 01 supported-PID bitmap - because every OBD-II
 * module answers it. The first version asked 22F190 and so tested "does this
 * address support mode 22", not "is anything there": a module that ignores an
 * unsupported service rather than refusing it looked exactly like an empty
 * address. A BMW reported its engine module as absent on that basis, on a car
 * that had answered a full scan minutes earlier.
 *
 * Mode 22 is then asked separately, so the two questions stay separate: present
 * is one fact, supports-manufacturer-data is another.
 */
export async function sweepModules(
  command: (cmd: string, timeoutMs?: number) => Promise<string>,
  modules: { header: string; name: string }[],
): Promise<ModulePresence[]> {
  const out: ModulePresence[] = [];

  for (const mod of modules) {
    await command(`ATSH${mod.header}`, 3_000).catch(() => '');

    // Is anything there at all.
    const hello = (await command('0100', 3_000).catch(() => '')).trim();
    const helloFlat = hello.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
    const present = helloFlat.includes('4100') || /7F01[0-9A-F]{2}/.test(helloFlat);

    // And separately, does it speak mode 22.
    let mode22: ModulePresence['mode22'] = 'unknown';
    let reply = hello;
    if (present) {
      const probe = (await command('22F190', 3_000).catch(() => '')).trim();
      const flat = probe.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
      if (flat.includes('62F190')) mode22 = 'yes';
      else if (/7F22(11|12)/.test(flat)) mode22 = 'no';
      else if (/7F22[0-9A-F]{2}/.test(flat)) mode22 = 'other-identifier';
      else mode22 = 'silent';
      reply = probe || hello;
    }

    out.push({
      header: mod.header,
      name: mod.name,
      state: present ? 'answered' : 'silent',
      mode22,
      raw: reply || null,
    });
  }

  return out;
}

/** Every address worth knocking on, across the makes in the fleet. */
export const SWEEP_MODULES: { header: string; name: string }[] = [
  { header: '7E0', name: 'Powertrain (standard OBD)' },
  { header: '7E1', name: 'Transmission (standard OBD)' },
  { header: '7E2', name: 'ECU 3 (standard OBD)' },
  { header: '7E3', name: 'ECU 4 (standard OBD)' },
  ...FORD_MODULES.filter((m) => !['7E0', '7E1'].includes(m.header)),
  ...BMW_MODULES.filter((m) => !['7E0', '7E1'].includes(m.header)),
];
